import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AttributeInterface } from 'src/app/model/attributes/attribute-interface';
import { PtgAttributeValidationInterface } from 'src/app/model/attributes/ptg-attribute-validation-interface';
import { ProductTypeGroupFeatureDisplayInterface, ProductTypeGroupFeaturesDisplayInterface } from 'src/app/model/marketplace/product-type-group-features-interface';
import { ProductVariantInterface } from 'src/app/model/products/product-variant-interface';
import { VariantVersionInterface } from 'src/app/model/products/variant-version-interface';
import { PtgVersionInterface } from 'src/app/model/ptgs/ptg-version-interface';

interface PTGFeatureComparison {
    ptgName: string;
    minimized: boolean;
    ptgDescription: string;
    isCertified?: boolean;
    variants: Array<{
      variantName: string;
      versionName: string;
      mandatoryFeatures: any[];
      optionalFeatures: any[];
      fulfillsAllMandatory: boolean;
      isCertified?: boolean;
    }>;
}

@Component({
    selector: 'app-product-type-group-feature-display',
    templateUrl: './product-type-group-feature-display.component.html',
    styleUrls: ['./product-type-group-feature-display.component.scss'],
    standalone: false
})
export class ProductTypeGroupFeatureDisplayComponent implements OnInit, OnChanges {
    readonly mandatoryFeaturesTooltip =
        'Verpflichtende Merkmale sind Funktionalitäten eines IT-Produkts, die von der BIMSWARM-Fachgruppe als Anforderungen definiert wurden, die jedes IT-Produkt dieses Produkttyps erfüllen sollte.';
    readonly optionalFeaturesTooltip =
        'Optionale Merkmale sind Funktionalitäten eines IT-Produkts, die von der BIMSWARM-Fachgruppe als hilfreich, aber nicht notwendig für IT-Produkte dieses Produkttyps definiert wurden.';

    @Input() ptgVersionFeatures?: ProductTypeGroupFeaturesDisplayInterface;
    @Input() variantsList?: ProductVariantInterface[]; // selected variant as one Object in array
    @Input() key?: any;
    @Input() comparisonMode?: 'version' | 'variant' = 'version';
    @Input() variantPTGFeaturesList?: any[]  = []; // array of all variants
    @Input() selectedVariantId?: number;

    minimized: boolean = true;
    ptgFeatureComparisonData: PTGFeatureComparison[] = [];
    ptgFeatureComparisonData2: PTGFeatureComparison[] = [];

    ngOnInit(): void {
        if (this.variantsList && this.variantsList.length > 0) {
            this.collectPTGFeaturesForComparison();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.variantsList) {
            if (changes.variantsList && changes.variantsList.currentValue.length >= 0) {
                this.collectPTGFeaturesForComparison();
            }  
        } else if (changes.key) {
            if (changes.variantsList && changes.variantsList.currentValue.length >= 0) {
                this.collectPTGFeaturesForComparison();
            }
        } else if (changes.variantPTGFeaturesList || changes.selectedVariantId) {
            if (this.variantPTGFeaturesList && this.variantPTGFeaturesList.length >= 0) {
                this.ptgFeatureComparisonData = this.sortVariantsBySelection(
                this.variantPTGFeaturesList,
                this.selectedVariantId
                );
                this.collectPTGFeaturesForComparison2();
            }  
        }
    }

    private getPTGFeatures(
        ptgVersion: PtgVersionInterface,
        version: VariantVersionInterface
    ): ProductTypeGroupFeaturesDisplayInterface {
        const ptgVersionFeatures: ProductTypeGroupFeaturesDisplayInterface = {
            ptgName: ptgVersion.ptgName,
            ptgDescription: this.removeHTMLTags(ptgVersion.ptgDescription),
            mandatoryFeatures: [],
            optionalFeatures: [],
            doesProjectFulfillAllFeaturesOfPTGVersion: true,
            order: ptgVersion?.order,
        };
        if(version.status === 'ACTIVE'){
        for (const attributeVal of ptgVersion.attributeValidations) {
            const feature: ProductTypeGroupFeatureDisplayInterface = {
                featureName: attributeVal.attribute.name,
                featureDescription: attributeVal.attribute.description,
                featureValidation: this.getValidationValue(attributeVal, version),
                datatype: attributeVal.attribute.datatype
            };
            if (attributeVal.optionalValidation) {
                ptgVersionFeatures.optionalFeatures.push(feature);
            } else {
                ptgVersionFeatures.mandatoryFeatures.push(feature);
                if (
                    !this.isAttributeValueFulfillingValidation(
                        attributeVal.attribute,
                        feature.featureValidation + ''
                    )
                ) {
                    ptgVersionFeatures.doesProjectFulfillAllFeaturesOfPTGVersion =
                        false;
                }
            }
        }
        if (ptgVersionFeatures.mandatoryFeatures.length === 0) {
            ptgVersionFeatures.doesProjectFulfillAllFeaturesOfPTGVersion =
                false;
        }
        ptgVersionFeatures.mandatoryFeatures.sort((a, b) =>
            a.featureName
                .toLowerCase()
                .localeCompare(b.featureName.toLowerCase())
        );
        ptgVersionFeatures.optionalFeatures.sort((a, b) =>
            a.featureName
                .toLowerCase()
                .localeCompare(b.featureName.toLowerCase())
        );
        return ptgVersionFeatures;
    }
    else return;
    }
    
    /**
     * Method sorts ptgs in version depending on given order or alphabetically
     * @param version 
     * @returns 
     */
    private updatePTGlist(version: VariantVersionInterface): any {
        const ptgs = [...(version.ptgVersions ?? [])];

        // Check if ptg has order defined in description, relevant for version creation or edit process
        const hasDescriptionOrder = ptgs.some(
            ptg => this.getOrderFromDescription(ptg.description) !== null);

        let sortedPtgVersions: any[];
        
        if (hasDescriptionOrder) {
            sortedPtgVersions = ptgs.sort((a, b) => {
                const orderA = this.getOrderFromDescription(a.description) ?? Number.MAX_SAFE_INTEGER;
                const orderB = this.getOrderFromDescription(b.description) ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
            });
        } else {
            // Check if any ptg has custom order property set, relevant for saved versions 
            const hasCustomOrder = ptgs.some(
                (v) => v.order && v.order > 0
            );

            sortedPtgVersions = hasCustomOrder
                ? [...ptgs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                : [...ptgs].sort((a, b) =>
                    a.ptgName.toLowerCase().localeCompare(b.ptgName.toLowerCase())
                );

        }

        const ptgVersionFeatures = sortedPtgVersions.map((ptgVersion) =>
            this.getPTGFeatures(ptgVersion, version)
        );
        const versionP = version;
        const ptg_names = version.productTypeNames;
        return [ptgVersionFeatures, versionP, ptg_names]
    }

    /**
     * Auxiliary method to detect if order is defined in description property of ptg
     * @param description - string property of ptg object
     * @returns 
     */
    private getOrderFromDescription(description?: string): number | null {
        if (!description) {
            return null;
        }
        const match = description.match(/^<<>>\s*(\d+)\s*,\s*(\d+)$/);
        if (!match) {
            return null;
        }
        return Number(match[2]); // second number is the order (first is the id)
    }

    // Comparison of all ptgs of all versions
    private collectPTGFeaturesForComparison() {
        const ptgMap: Map<string, PTGFeatureComparison> = new Map();
    
        this.variantsList.forEach(variant => {
            variant.variantVersions.forEach(version => {
                const [ptgVersionFeatures, versionP, ptg_names] = this.updatePTGlist(version); 
                if(versionP.status === 'ACTIVE') {
                    ptgVersionFeatures.forEach(ptg => {
                        if (!ptg) return;
                        const ptgKey = ptg.ptgName;
                        const mandatoryFeatures = ptg.mandatoryFeatures || [];
                        const optionalFeatures = ptg.optionalFeatures || [];
                        const fulfillsAllMandatory = ptg.doesProjectFulfillAllFeaturesOfPTGVersion || false;
                
                        if (!ptgMap.has(ptgKey)) {
                            ptgMap.set(ptgKey, {
                            ptgName: ptg.ptgName,
                            ptgDescription: ptg.ptgDescription,
                            minimized: true,
                            isCertified: false,
                            variants: []
                            });
                        }
                        ptgMap.get(ptgKey).isCertified ||= this.isPtgCertified(ptg.ptgName, version);
                        ptgMap.get(ptgKey).variants.push({
                            variantName: variant.name,
                            versionName: version.name,
                            mandatoryFeatures,
                            optionalFeatures,
                            fulfillsAllMandatory,
                            isCertified: this.isPtgCertified(ptg.ptgName, version)
                        });
                    });
                }
            });
        
        });
        this.ptgFeatureComparisonData = Array.from(ptgMap.values());
    }


    private collectPTGFeaturesForComparison2() {
        const ptgMap: Map<string, PTGFeatureComparison> = new Map();

        // this.ptgFeatureComparisonData is defined in  ngOnChanges
        this.ptgFeatureComparisonData.forEach((variant: any) => {
            // variant.variantVersions.forEach(version => {
                const version = this.getLatestVersionById(variant);
                if (!version) return; 
                const [ptgVersionFeatures, versionP, ptg_names] = this.updatePTGlist(version); 
                if(versionP.status === 'ACTIVE'){
                    ptgVersionFeatures.forEach(ptg => {
                        if (!ptg) return; 
                        const ptgKey = ptg.ptgName;
                        const mandatoryFeatures = ptg.mandatoryFeatures || [];
                        const optionalFeatures = ptg.optionalFeatures || [];
                        const fulfillsAllMandatory = ptg.doesProjectFulfillAllFeaturesOfPTGVersion || false;
                
                        if (!ptgMap.has(ptgKey)) {
                            ptgMap.set(ptgKey, {
                            ptgName: ptg.ptgName,
                            ptgDescription: ptg.ptgDescription,
                            minimized: true,
                            isCertified: false,
                            variants: []
                            });
                        }
                        ptgMap.get(ptgKey).isCertified ||= this.isPtgCertified(ptg.ptgName, version);
                        ptgMap.get(ptgKey).variants.push({
                            variantName: variant.name,
                            versionName: version.name,
                            mandatoryFeatures,
                            optionalFeatures,
                            fulfillsAllMandatory,
                            isCertified: this.isPtgCertified(ptg.ptgName, version)
                        });
                    });
                }
            // });
        });
        this.ptgFeatureComparisonData2 = Array.from(ptgMap.values());  
    }

    private getLatestVersionById(variant: ProductVariantInterface): VariantVersionInterface | null {
        const allVersions: VariantVersionInterface[] = [];

        if (variant.variantVersions && variant.variantVersions.length > 0) {
            allVersions.push(...variant.variantVersions);
        }
        if (allVersions.length === 0) return null;

        const sortedVersions = [...allVersions].sort((a, b) => a.id - b.id);
        const lastVersion = sortedVersions[sortedVersions.length - 1];

        if (lastVersion?.status === 'ACTIVE') {
        return lastVersion;
        }

        const previousActive = [...sortedVersions]
        .reverse()
        .find((v) => v.status === 'ACTIVE');

        return previousActive ?? null;
    }


    public toggleMinimization(): void {
        this.minimized = !this.minimized;
    }

    toggleMinimization2(index: number): void {
        this.ptgFeatureComparisonData[index].minimized = !this.ptgFeatureComparisonData[index].minimized;
    }

    toggleMinimization3(index: number): void {
        this.ptgFeatureComparisonData2[index].minimized = !this.ptgFeatureComparisonData2[index].minimized;
    }

    hasMandatoryFeatures(): boolean {
        return this.ptgVersionFeatures.mandatoryFeatures.length !== 0;
    }

    hasOptionalFeatures(): boolean {
        return this.ptgVersionFeatures.optionalFeatures.length !== 0;
    }

    ptghasMandatoryFeatures(ptg: any): boolean {
        return ptg.mandatoryFeatures.length !== 0;
    }

    ptghasOptionalFeatures(ptg: any): boolean {
        return ptg.optionalFeatures.length !== 0;
    }

    private removeHTMLTags(text: string): string {
        return text.replace(/<[^>]*>/g, '');
    }

    private getValidationValue(
        attribute: PtgAttributeValidationInterface,
        version: VariantVersionInterface
    ): string {
        const validation = version.attributeValidationValues?.find(
            (validationValue) =>
                validationValue.attribute.id === attribute.attribute.id
        );
        const validationValue = validation == null ? '' : validation.value;
        if (
            attribute.attribute.datatype === 'LIST' &&
            validationValue != null
        ) {
            return validationValue.replace(/;/g, ', ');
        }
        return validationValue;
    }

    private isAttributeValueFulfillingValidation(
        attribute: AttributeInterface,
        value: string
    ) {
        const validation = attribute.validations[0];
        switch (attribute.datatype) {
            case 'NUMERIC':
                return this.isNumberInRange(
                    Number(value),
                    validation.minSize,
                    validation.maxSize
                );
            case 'BOOLEAN':
                return (
                    attribute.validations[0].value == null ||
                    value == null ||
                    attribute.validations[0].value + '' == value + ''
                );
            case 'LIST':
                return this.isListValid(
                    validation.values,
                    validation.minSize,
                    validation.maxSize,
                    value.split(', ')
                );
            case 'STRING':
                return this.isNumberInRange(
                    value.length,
                    validation.minSize,
                    validation.maxSize
                );
        }
    }

    private isListValid(
        validationValues: string[],
        minCount: number,
        maxCount: number,
        selectedValues: string[]
    ): boolean {
        for (const selectedValue of selectedValues) {
            if (!validationValues.includes(selectedValue)) {
                return false;
            }
        }
        return this.isNumberInRange(selectedValues.length, minCount, maxCount);
    }

    private isNumberInRange(value: number, min: number, max: number): boolean {
        return (min == null || min <= value) && (max == null || max >= value);
    }
   
    private doesProjectFulfillAllFeaturesOfPTGVersion(ptgVariants: any[]): boolean {
        return ptgVariants.some(
            variant => variant.fulfillsAllMandatory === true
        );
    }

    // Collect all unique features across all variants
    getAllVariantFeatures(): string[] {
        const featureSet = new Set<string>();
        for (const variant of this.variantPTGFeaturesList) {
            for (const version of variant.variantVersions) {
                for (const ptgVersion of version.ptgVersions) {
                    for (const attrVal of ptgVersion.attributeValidations) {
                        featureSet.add(attrVal.attribute.name);
                    }
                }
            }
        }
        return Array.from(featureSet).sort();
    }
  
    // Get the value of a feature for a given variant (first version, first PTG, first validation found)
    getVariantFeatureValue(variant: any, featureName: string): string {
        for (const version of variant.variantVersions) {
            for (const ptgVersion of version.ptgVersions) {
                for (const attrVal of ptgVersion.attributeValidations) {
                    if (attrVal.attribute.name === featureName) {
                        return attrVal.attribute.datatype === 'BOOLEAN'
                        ? (attrVal.attribute.validations?.[0]?.value ? 'true' : 'false')
                        : attrVal.attribute.validations?.[0]?.value ?? '';
                    }
                }
            }
        }
        return '–';
    }

    
    private sortVariantsBySelection(variants: any[], selectedId?: number): any[] {
        if (!selectedId || !variants || variants.length === 0) {
            return variants;
        }
        
        const selectedVariant = variants.find(v => v.id === selectedId);
        
        const otherVariants = variants.filter(v => v.id !== selectedId);
        
        if (selectedVariant) {
            return [selectedVariant, ...otherVariants];
        }
        
        return variants;
    }

    trackById(index: number, item: any): number {
        return item.ptgName;
    }

    private isPtgCertified(ptgName: string, version: VariantVersionInterface): boolean {
        return (version.certificationVariantVersions ?? []).some(
            (certVersion) =>
                (certVersion.ptgVersions ?? []).some(
                    (certPtg) => certPtg.ptgName === ptgName
                )
        );
    }
}
